// Компактный переключатель языка — единый по всему приложению (Topbar,
// /legal/accept, onboarding). Globe-иконка + код локали + меню со списком.
// Combobox-семантика (готов к 14+ языков), в компактном тактильном стиле
// «Картотека». Источник истины локали — i18next через useLocale.
//
// Платформенно-адаптивный: на desktop — Radix dropdown (компактные пункты),
// на mobile/touch — нативная нижняя шторка BottomSheet с крупными строками
// (≥48px). Оба режима — через общий `ActionMenu`.
//
// ─────────────────────────────────────────────────────────────────────────────
// На публичных страницах переключатель меняет АДРЕС, а не только состояние
// ─────────────────────────────────────────────────────────────────────────────
// `/de/catalog` → испанский → `/es/catalog`; обратно на английский → голый
// `/catalog` без префикса. Иначе языковая версия остаётся невыразимой ссылкой:
// поделиться ей нельзя и индексировать нечего.
//
// Переход — полной загрузкой документа, а не навигацией роутера. Причина не в
// удобстве: голову публичной страницы (`<html lang>`, title, description,
// canonical, кластер hreflang, og:locale) рисует СЕРВЕР по адресу. Навигация
// внутри SPA сменила бы адрес, оставив голову от прежнего языка, — ровно тот
// дефект, ради которого всё и затевалось. Плюс роутер считает переход «на то же
// место» (внутренний путь-то один и тот же) и адрес в строке не обновил бы.
//
// Внутри приложения (закрытая часть, шлюзы согласия и онбординга) адрес
// префикса не имеет — там поведение прежнее: сменили язык, остались на месте.
//
// То же самое — у ВОШЕДШЕГО, где бы он ни стоял, включая `/` и `/catalog`:
// языковой адрес существует только для гостя (шапка `lib/locale-url.ts`).
// Без этой проверки переключатель внутри сервиса сам возвращал бы префикс в
// адрес, который только что был из него убран, — и ещё перезагружал документ.

import { IconChevronDown, IconWorld } from "@tabler/icons-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ActionMenu, type ActionMenuItem } from "@/components/shared/action-menu";
import { usePersistLocale } from "@/hooks/use-persist-locale";
import { useSession } from "@/hooks/use-session";
import { useSupportedLocales } from "@/hooks/use-supported-locales";
import { LocaleFlag } from "@/i18n/locale-flags";
import type { SupportedLocale } from "@/i18n/locales";
import { useLocale } from "@/i18n/use-locale";
import { cn } from "@/lib/cn";
import { addressCarriesLocale, resolveLocaleSwitchTarget } from "@/lib/locale-url";
import { IS_CAPACITOR } from "@/lib/platform-detect";

const TRIGGER_CLASS = cn(
	"size-9 cursor-pointer rounded-[9px] bg-transparent text-foreground",
	"transition-[background-color,transform] duration-fast",
	"hover:bg-muted active:translate-y-px",
	"inline-flex items-center justify-center gap-1 px-2 w-auto min-w-9",
	"text-[12px] font-semibold tracking-wide uppercase tabular-nums",
	// На touch зона нажатия растёт до 44px (визуальный размер не меняется).
	"pointer-coarse:min-h-11 pointer-coarse:min-w-11",
);

export function LocaleSwitcher() {
	const { t } = useTranslation("layout");
	const { locale } = useLocale();
	const persistLocale = usePersistLocale();
	const { locales } = useSupportedLocales();
	const { isAuthenticated } = useSession();

	const selectLocale = useCallback(
		async (next: SupportedLocale) => {
			// Выбор сохраняется ВСЕГДА и первым: он нужен и здесь, и серверному
			// редиректу с голых путей, а после перехода страница уже другая.
			await persistLocale(next);
			// В приложении языковых адресов нет и быть не может: origin WebView —
			// `https://localhost`, индексировать там нечего, а перезагрузка документа
			// стоит холодного старта (переинициализация PowerSync и всего каркаса).
			// Каталог виден и внутри приложения, поэтому проверка именно здесь.
			if (IS_CAPACITOR) return;
			// Вошедший — тоже «внутри сервиса», даже на `/` и `/catalog`.
			if (!addressCarriesLocale(isAuthenticated)) return;
			const target = resolveLocaleSwitchTarget(window.location, next);
			if (target) window.location.assign(target);
		},
		[persistLocale, isAuthenticated],
	);

	const items: ActionMenuItem[] = locales.map((opt) => ({
		key: opt.code,
		label: opt.nativeName,
		icon: <LocaleFlag emoji={opt.flagEmoji} />,
		selected: opt.code === locale,
		onSelect: () => void selectLocale(opt.code as SupportedLocale),
	}));
	return (
		<ActionMenu
			align="end"
			title={t("language.label")}
			items={items}
			contentClassName="min-w-[160px]"
			trigger={
				<button type="button" aria-label={t("language.label")} className={TRIGGER_CLASS}>
					<IconWorld size={16} stroke={1.75} aria-hidden />
					<span>{locale}</span>
					<IconChevronDown size={12} stroke={2} aria-hidden />
				</button>
			}
		/>
	);
}
